import { useState, useEffect, useCallback, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import categoryService from '../../services/categoryService';
import productService from '../../services/productService';
import RichTextEditor from '../common/RichTextEditor';
import ImageUpload from './ImageUpload';
import FeaturesEditor from './FeaturesEditor';
import TagsInput from './TagsInput';
import ProductPreview from './ProductPreview';
import { saveTemplate, getTemplates, deleteTemplate, applyTemplate } from '../../utils/productTemplates';
import './ProductForm.css';

const schema = yup.object({
  name: yup.string()
    .required('Product name is required')
    .min(3, 'Name must be at least 3 characters')
    .max(200, 'Name must be less than 200 characters'),
  sku: yup.string()
    .required('SKU is required')
    .max(50, 'SKU must be less than 50 characters')
    .matches(/^[A-Z0-9-]+$/i, 'SKU can only contain letters, numbers, and hyphens'),
  description: yup.string()
    .required('Description is required')
    .min(10, 'Description must be at least 10 characters'),
  shortDescription: yup.string()
    .max(200, 'Short description must be less than 200 characters')
    .nullable(),
  price: yup.number()
    .required('Price is required')
    .positive('Price must be positive')
    .max(1000000, 'Price seems unusually high')
    .typeError('Price must be a number'),
  compareAtPrice: yup.number()
    .nullable()
    .positive('Compare at price must be positive')
    .typeError('Must be a number')
    .transform((value, originalValue) => originalValue === '' ? null : value)
    .test('greater-than-price', 'Compare at price should be greater than regular price', function(value) {
      const { price } = this.parent;
      if (!value || !price) return true;
      return value > price;
    }),
  category: yup.string().required('Category is required'),
  status: yup.string().required('Status is required'),
  stock: yup.number()
    .required('Stock is required')
    .min(0, 'Stock cannot be negative')
    .integer('Stock must be a whole number')
    .max(1000000, 'Stock seems unusually high')
    .typeError('Stock must be a number'),
  lowStockThreshold: yup.number()
    .min(0, 'Threshold cannot be negative')
    .integer('Must be a whole number')
    .typeError('Must be a number'),
  isLimitedEdition: yup.boolean(),
  editionSize: yup.number()
    .nullable()
    .positive('Edition size must be positive')
    .integer('Must be a whole number')
    .typeError('Must be a number')
    .transform((value, originalValue) => originalValue === '' ? null : value),
  artist: yup.string().max(100, 'Artist name must be less than 100 characters'),
  dimensions: yup.string().max(100, 'Dimensions must be less than 100 characters'),
  materials: yup.string().max(200, 'Materials must be less than 200 characters'),
  hasAudioDescription: yup.boolean(),
  hasVideoContent: yup.boolean(),
  isFeatured: yup.boolean(),
  videoUrl: yup.string()
    .url('Must be a valid URL')
    .max(500, 'URL is too long')
    .nullable()
    .transform((value, originalValue) => originalValue === '' ? null : value),
  videoType: yup.string()
    .max(50)
    .nullable()
    .transform((value, originalValue) => originalValue === '' ? null : value),
  videoThumbnailUrl: yup.string()
    .url('Must be a valid URL')
    .max(500, 'URL is too long')
    .nullable()
    .transform((value, originalValue) => originalValue === '' ? null : value),
  audioUrl: yup.string()
    .url('Must be a valid URL')
    .max(500, 'URL is too long')
    .nullable()
    .transform((value, originalValue) => originalValue === '' ? null : value),
  tactileMockupUrl: yup.string()
    .url('Must be a valid URL')
    .max(500, 'URL is too long')
    .nullable()
    .transform((value, originalValue) => originalValue === '' ? null : value),
  aboutTheArt: yup.string().nullable(),
  additionalInformation: yup.string().nullable(),
}).required();

function ProductForm({ product, onSubmit, isSubmitting }) {
  const [categories, setCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [categoriesError, setCategoriesError] = useState(null);
  const [activeTab, setActiveTab] = useState('basic');
  const [images, setImages] = useState([]);
  const [features, setFeatures] = useState([]);
  const [tags, setTags] = useState([]);
  const isEditing = !!product;

  // Advanced validation states
  const [skuValidation, setSkuValidation] = useState({ checking: false, isUnique: null, message: '' });
  const skuTimeoutRef = useRef(null);

  // Auto-save states
  const [saveStatus, setSaveStatus] = useState('saved'); // 'saved', 'saving', 'unsaved', 'error'
  const [lastSaved, setLastSaved] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const autoSaveTimerRef = useRef(null);
  const formValuesRef = useRef(null);

  // Preview state
  const [showPreview, setShowPreview] = useState(false);

  // Template states
  const [showSaveTemplateModal, setShowSaveTemplateModal] = useState(false);
  const [showLoadTemplateModal, setShowLoadTemplateModal] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [templates, setTemplates] = useState([]);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      sku: '',
      description: '',
      shortDescription: '',
      price: '',
      compareAtPrice: '',
      category: '',
      status: 'DRAFT',
      stock: 0,
      lowStockThreshold: 5,
      isLimitedEdition: false,
      editionSize: '',
      artist: '',
      dimensions: '',
      materials: '',
      hasAudioDescription: false,
      hasVideoContent: false,
      isFeatured: false,
      videoUrl: '',
      videoType: '',
      videoThumbnailUrl: '',
      audioUrl: '',
      tactileMockupUrl: '',
      aboutTheArt: '',
      additionalInformation: '',
    },
  });

  const isLimitedEdition = watch('isLimitedEdition');
  const currentSku = watch('sku');
  const currentName = watch('name');
  const currentShortDescription = watch('shortDescription');

  // Debounced SKU uniqueness check
  const checkSkuUniqueness = useCallback(async (sku) => {
    if (!sku || sku.trim() === '') {
      setSkuValidation({ checking: false, isUnique: null, message: '' });
      return;
    }

    // Don't check if editing and SKU hasn't changed
    if (isEditing && product?.sku === sku) {
      setSkuValidation({ checking: false, isUnique: true, message: 'Current SKU' });
      return;
    }

    setSkuValidation({ checking: true, isUnique: null, message: 'Checking availability...' });

    // Log the SKU being checked
    console.log('[SKU Validation] Checking SKU:', sku);

    try {
      const response = await productService.checkSkuUniqueness(sku, product?.id);

      // Backend returns: { success: true, data: { isUnique: true, sku: "..." } }
      // So we need to access response.data.data.isUnique, not response.data.isUnique
      const isUnique = response.data.data?.isUnique;

      console.log('[SKU Validation] Full response:', response.data);
      console.log('[SKU Validation] Extracted isUnique:', isUnique);

      // RACE CONDITION FIX: Only update state if this response is for the current SKU
      // If user has typed more since this request started, ignore this response
      const currentSkuValue = watch('sku');
      if (currentSkuValue !== sku) {
        console.log('[SKU Validation] RACE: Response for', sku, 'but current SKU is', currentSkuValue, '- ignoring');
        return;
      }

      console.log('[SKU Validation] Response for', sku, '- isUnique:', isUnique);

      setSkuValidation({
        checking: false,
        isUnique,
        message: isUnique ? 'SKU is available' : 'SKU is already in use',
      });
    } catch (err) {
      console.error('[SKU Validation] Error checking SKU uniqueness:', err);

      // RACE CONDITION FIX: Only update state if this error is for the current SKU
      const currentSkuValue = watch('sku');
      if (currentSkuValue !== sku) {
        console.log('[SKU Validation] RACE: Error for', sku, 'but current SKU is', currentSkuValue, '- ignoring');
        return;
      }

      // Provide user-friendly error message based on error type
      let userMessage = 'Unable to verify SKU availability at this time. You can continue, but the SKU may be duplicated.';

      // Network error
      if (!err.response) {
        userMessage = 'Cannot connect to server. Please check your internet connection and try again.';
      }
      // Server error (500)
      else if (err.response.status >= 500) {
        userMessage = 'Server is temporarily unavailable. You can continue, but the SKU may be duplicated.';
      }
      // Other errors
      else if (err.response.status >= 400) {
        userMessage = 'Unable to verify SKU. Please try a different SKU or contact support if this persists.';
      }

      setSkuValidation({
        checking: false,
        isUnique: null,
        message: userMessage,
      });
    }
  }, [isEditing, product, watch]);

  // Watch SKU field and debounce validation
  useEffect(() => {
    if (skuTimeoutRef.current) {
      clearTimeout(skuTimeoutRef.current);
    }

    if (currentSku) {
      // IMPORTANT: Clear old validation state immediately when SKU changes
      // This prevents stale "already in use" messages from showing while typing
      setSkuValidation({ checking: true, isUnique: null, message: 'Checking availability...' });

      skuTimeoutRef.current = setTimeout(() => {
        checkSkuUniqueness(currentSku);
      }, 500); // 500ms debounce
    } else {
      setSkuValidation({ checking: false, isUnique: null, message: '' });
    }

    return () => {
      if (skuTimeoutRef.current) {
        clearTimeout(skuTimeoutRef.current);
      }
    };
  }, [currentSku, checkSkuUniqueness]);

  // Character counter helper
  const getCharacterCount = (text, max) => {
    const current = text ? text.length : 0;
    const percentage = (current / max) * 100;
    const isNearLimit = percentage >= 80;
    const isAtLimit = percentage >= 100;
    return { current, max, percentage, isNearLimit, isAtLimit };
  };

  // Auto-save helpers
  const getLocalStorageKey = () => {
    return isEditing ? `product-draft-${product.id}` : 'product-draft-new';
  };

  const saveToLocalStorage = useCallback((data) => {
    try {
      const draftData = {
        ...data,
        images,
        features,
        tags,
        timestamp: Date.now(),
      };
      localStorage.setItem(getLocalStorageKey(), JSON.stringify(draftData));
    } catch (err) {
      console.error('Failed to save draft to local storage:', err);
    }
  }, [images, features, tags, isEditing, product]);

  const loadFromLocalStorage = useCallback(() => {
    try {
      const saved = localStorage.getItem(getLocalStorageKey());
      if (saved) {
        const draftData = JSON.parse(saved);
        // Check if draft is less than 7 days old
        const sevenDays = 7 * 24 * 60 * 60 * 1000;
        if (Date.now() - draftData.timestamp < sevenDays) {
          return draftData;
        } else {
          // Clean up old draft
          localStorage.removeItem(getLocalStorageKey());
        }
      }
    } catch (err) {
      console.error('Failed to load draft from local storage:', err);
    }
    return null;
  }, [isEditing, product]);

  const clearLocalStorage = useCallback(() => {
    try {
      localStorage.removeItem(getLocalStorageKey());
    } catch (err) {
      console.error('Failed to clear local storage:', err);
    }
  }, [isEditing, product]);

  const performAutoSave = useCallback(async () => {
    if (!isDirty || !isEditing) return; // Only auto-save when editing existing products

    const currentValues = watch();

    // Don't save if there are validation errors
    const hasErrors = Object.keys(errors).length > 0;
    if (hasErrors) return;

    setSaveStatus('saving');

    try {
      // Save to local storage
      saveToLocalStorage(currentValues);

      // Optionally save to server as draft
      // This would require a separate API endpoint for draft saves
      // await productService.saveDraft(product.id, currentValues);

      setSaveStatus('saved');
      setLastSaved(new Date());
      setIsDirty(false);
    } catch (err) {
      console.error('Auto-save failed:', err);
      setSaveStatus('error');
    }
  }, [isDirty, isEditing, watch, errors, saveToLocalStorage, product]);

  useEffect(() => {
    const fetchCategories = async () => {
      setCategoriesLoading(true);
      setCategoriesError(null);
      try {
        const data = await categoryService.getCategories();
        // Filter only active categories for product form
        const activeCategories = data.filter(cat => cat.isActive);
        setCategories(activeCategories);
      } catch (err) {
        console.error('Error fetching categories:', err);
        setCategoriesError('Failed to load categories. Please refresh the page.');
      } finally {
        setCategoriesLoading(false);
      }
    };
    fetchCategories();
  }, []);

  // Watch for form changes to mark as dirty
  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      if (type === 'change' && name) {
        setIsDirty(true);
        setSaveStatus('unsaved');
      }
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  // Watch for images, features, tags changes
  useEffect(() => {
    if (formValuesRef.current) {
      setIsDirty(true);
      setSaveStatus('unsaved');
    }
    formValuesRef.current = { images, features, tags };
  }, [images, features, tags]);

  // Auto-save timer
  useEffect(() => {
    if (isDirty && isEditing) {
      // Clear existing timer
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }

      // Set new timer for 30 seconds
      autoSaveTimerRef.current = setTimeout(() => {
        performAutoSave();
      }, 30000); // 30 seconds

      return () => {
        if (autoSaveTimerRef.current) {
          clearTimeout(autoSaveTimerRef.current);
        }
      };
    }
  }, [isDirty, isEditing, performAutoSave]);

  // Warn before leaving page with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDirty]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const modifierKey = isMac ? e.metaKey : e.ctrlKey;

      // Ctrl/Cmd + S: Save form
      if (modifierKey && e.key === 's') {
        e.preventDefault();
        handleSubmit(handleFormSubmit)();
      }

      // Ctrl/Cmd + Enter: Quick publish (set to ACTIVE and save)
      if (modifierKey && e.key === 'Enter') {
        e.preventDefault();
        setValue('status', 'ACTIVE');
        // Small delay to ensure status is updated
        setTimeout(() => {
          handleSubmit(handleFormSubmit)();
        }, 50);
      }

      // Escape: Close preview modal
      if (e.key === 'Escape' && showPreview) {
        e.preventDefault();
        setShowPreview(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleSubmit, showPreview, setValue]);

  useEffect(() => {
    if (product) {
      reset({
        name: product.name || '',
        sku: product.sku || '',
        description: product.description || '',
        shortDescription: product.shortDescription || '',
        price: product.price || '',
        compareAtPrice: product.compareAtPrice || '',
        category: product.category?.id || product.categoryId || product.category || '',
        status: product.status || 'DRAFT',
        stock: product.stock ?? 0,
        lowStockThreshold: product.lowStockThreshold ?? 5,
        isLimitedEdition: product.isLimitedEdition || false,
        editionSize: product.editionSize || '',
        artist: product.artist || '',
        dimensions: product.dimensions || '',
        materials: product.materials || '',
        hasAudioDescription: product.hasAudioDescription || false,
        hasVideoContent: product.hasVideoContent || false,
        isFeatured: product.isFeatured || false,
        videoUrl: product.videoUrl || '',
        videoType: product.videoType || '',
        videoThumbnailUrl: product.videoThumbnailUrl || '',
        audioUrl: product.audioUrl || '',
        tactileMockupUrl: product.tactileMockupUrl || '',
        aboutTheArt: product.aboutTheArt || '',
        additionalInformation: product.additionalInformation || '',
      });

      // Set images if editing existing product
      if (product.images && Array.isArray(product.images)) {
        setImages(product.images.map(img => ({
          ...img,
          isNew: false,
        })));
      }

      // Set features if editing existing product
      // Backend sends features as array of strings: ["Feature 1", "Feature 2"]
      if (product.features && Array.isArray(product.features)) {
        setFeatures(product.features.map((feature, index) => ({
          id: `feature-${index}`,
          text: typeof feature === 'string' ? feature : (feature.text || feature.value || '')
        })));
      }

      // Set tags if editing existing product
      // Backend sends tags as comma-separated string: "tag1,tag2,tag3"
      if (product.tags) {
        const tagsArray = typeof product.tags === 'string'
          ? product.tags.split(',').map(t => t.trim()).filter(t => t)
          : Array.isArray(product.tags)
            ? product.tags
            : [];
        setTags(tagsArray);
      }
    }
  }, [product, reset]);

  const handleFormSubmit = (data) => {
    // Find category by ID to get the categoryId for API
    const selectedCategory = categories.find(c => c.id === data.category);

    // Validate features - ensure no empty text
    const validFeatures = features.filter(f => f.text && f.text.trim());

    // Clean up data before submitting
    const cleanedData = {
      ...data,
      categoryId: selectedCategory?.id || data.category, // Use categoryId for API
      compareAtPrice: data.compareAtPrice || null,
      editionSize: data.isLimitedEdition ? data.editionSize : null,
      slug: data.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, ''),
      images: images.map((img, index) => ({
        id: img.isNew ? undefined : img.id,
        url: img.url,
        isPrimary: img.isPrimary,
        displayOrder: index,
        isNew: img.isNew,  // ← CRITICAL: Preserve isNew flag for upload detection!
        file: img.file,    // ← Always include file (processImages will check isNew)
        altText: img.altText,
      })),
      // Backend expects features as array of strings: ["Feature 1", "Feature 2"]
      features: validFeatures.map(feature => feature.text.trim()),
      // Backend expects tags as comma-separated string: "tag1,tag2,tag3"
      tags: tags.filter(tag => tag && tag.trim()).map(tag => tag.trim()).join(','),
    };

    // Clear local storage and reset dirty state after submit
    clearLocalStorage();
    setIsDirty(false);
    setSaveStatus('saved');

    // Log data structure for debugging (can be removed in production)
    console.log('Submitting product data:', cleanedData);

    onSubmit(cleanedData);
  };

  // Template handlers
  const handleSaveAsTemplate = () => {
    if (!templateName.trim()) {
      toast.error('Please enter a template name');
      return;
    }

    try {
      const currentData = {
        ...watch(),
        images,
        features,
        tags,
      };

      saveTemplate(templateName, currentData);
      toast.success(`Template "${templateName}" saved successfully`);
      setShowSaveTemplateModal(false);
      setTemplateName('');
      setTemplates(getTemplates()); // Refresh template list
    } catch (err) {
      toast.error('Failed to save template');
      console.error('Error saving template:', err);
    }
  };

  const handleLoadTemplate = (templateId) => {
    try {
      const templateData = applyTemplate(templateId);
      if (!templateData) {
        toast.error('Template not found');
        return;
      }

      // Apply template data to form
      Object.keys(templateData).forEach((key) => {
        if (key === 'features') {
          setFeatures(templateData.features || []);
        } else if (key === 'tags') {
          setTags(templateData.tags || []);
        } else if (key === 'images') {
          setImages(templateData.images || []);
        } else {
          setValue(key, templateData[key]);
        }
      });

      toast.success('Template applied successfully');
      setShowLoadTemplateModal(false);
      setIsDirty(true); // Mark as dirty since we changed the form
    } catch (err) {
      toast.error('Failed to load template');
      console.error('Error loading template:', err);
    }
  };

  const handleDeleteTemplate = (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      deleteTemplate(templateId);
      toast.success('Template deleted');
      setTemplates(getTemplates()); // Refresh template list
    } catch (err) {
      toast.error('Failed to delete template');
      console.error('Error deleting template:', err);
    }
  };

  // Load templates on mount
  useEffect(() => {
    setTemplates(getTemplates());
  }, []);

  // Helper function to render category options with hierarchy
  const renderCategoryOptions = () => {
    if (categories.length === 0) return null;

    // Build tree structure
    const tree = categoryService.buildTree(categories);

    // Flatten tree with indentation levels
    const flattenWithLevel = (nodes, level = 0) => {
      let result = [];
      for (const node of nodes) {
        result.push({ ...node, level });
        if (node.children && node.children.length > 0) {
          result = result.concat(flattenWithLevel(node.children, level + 1));
        }
      }
      return result;
    };

    const flatCategories = flattenWithLevel(tree);

    return flatCategories.map((cat) => {
      const indent = '  '.repeat(cat.level) + (cat.level > 0 ? '└─ ' : '');
      const icon = cat.icon || '';
      const label = `${indent}${icon}${icon ? ' ' : ''}${cat.name}`;
      return (
        <option key={cat.id} value={cat.id}>
          {label}
        </option>
      );
    });
  };

  return (
    <form className="product-form" onSubmit={handleSubmit(handleFormSubmit)}>
      {/* Tab Navigation */}
      <div className="form-tabs">
        <button
          type="button"
          className={`tab ${activeTab === 'basic' ? 'active' : ''}`}
          onClick={() => setActiveTab('basic')}
        >
          Basic Info
        </button>
        <button
          type="button"
          className={`tab ${activeTab === 'content' ? 'active' : ''}`}
          onClick={() => setActiveTab('content')}
        >
          Content
        </button>
        <button
          type="button"
          className={`tab ${activeTab === 'media' ? 'active' : ''}`}
          onClick={() => setActiveTab('media')}
        >
          Media
        </button>
        <button
          type="button"
          className={`tab ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          Settings
        </button>

        {/* Save Status Indicator */}
        {isEditing && (
          <div className="save-status">
            {saveStatus === 'saving' && (
              <span className="status-indicator saving">
                <svg className="spinner-small" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </span>
            )}
            {saveStatus === 'saved' && lastSaved && (
              <span className="status-indicator saved">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Saved {new Date(lastSaved).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
            )}
            {saveStatus === 'unsaved' && (
              <span className="status-indicator unsaved">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                </svg>
                Unsaved changes
              </span>
            )}
            {saveStatus === 'error' && (
              <span className="status-indicator error">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                </svg>
                Auto-save failed
              </span>
            )}
          </div>
        )}
      </div>

      {/* Tab Panel: Basic Info */}
      {activeTab === 'basic' && (
        <div className="form-grid single-column">
          <div className="form-column main-column">
            <div className="form-section">
              <h3>Basic Information</h3>

              <div className="form-group">
                <label htmlFor="name" className="form-label required">
                  Product Name
                  {(() => {
                    const count = getCharacterCount(currentName, 200);
                    return (
                      <span className={`char-counter ${count.isNearLimit ? 'warning' : ''} ${count.isAtLimit ? 'error' : ''}`}>
                        {count.current}/{count.max}
                      </span>
                    );
                  })()}
                </label>
                <div className="input-with-icon">
                  <input
                    id="name"
                    type="text"
                    className={`form-input ${errors.name ? 'error' : ''}`}
                    placeholder="Enter product name"
                    {...register('name')}
                  />
                  {!errors.name && currentName && currentName.length >= 3 && (
                    <span className="validation-icon success">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </span>
                  )}
                </div>
                {errors.name && <span className="form-error">{errors.name.message}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="sku" className="form-label required">SKU</label>
                <div className="input-with-icon">
                  <input
                    id="sku"
                    type="text"
                    className={`form-input ${errors.sku ? 'error' : ''} ${skuValidation.isUnique === false ? 'error' : ''}`}
                    placeholder="e.g., TM-001"
                    {...register('sku')}
                  />
                  {skuValidation.checking && (
                    <span className="validation-icon checking">
                      <svg className="spinner" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </span>
                  )}
                  {!skuValidation.checking && skuValidation.isUnique === true && (
                    <span className="validation-icon success">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </span>
                  )}
                  {!skuValidation.checking && skuValidation.isUnique === false && (
                    <span className="validation-icon error">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </span>
                  )}
                </div>
                {errors.sku && <span className="form-error">{errors.sku.message}</span>}
                {!errors.sku && skuValidation.message && (
                  <span className={`form-hint ${skuValidation.isUnique === true ? 'success' : ''} ${skuValidation.isUnique === false ? 'error' : ''}`}>
                    {skuValidation.message}
                  </span>
                )}
                {!errors.sku && !skuValidation.message && (
                  <span className="form-hint">
                    Unique identifier for this product. Can contain letters, numbers, and hyphens.
                  </span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="shortDescription" className="form-label">
                  Short Description
                  {(() => {
                    const count = getCharacterCount(currentShortDescription, 200);
                    return (
                      <span className={`char-counter ${count.isNearLimit ? 'warning' : ''} ${count.isAtLimit ? 'error' : ''}`}>
                        {count.current}/{count.max}
                      </span>
                    );
                  })()}
                </label>
                <input
                  id="shortDescription"
                  type="text"
                  className={`form-input ${errors.shortDescription ? 'error' : ''}`}
                  placeholder="Brief product summary"
                  {...register('shortDescription')}
                />
                {errors.shortDescription && <span className="form-error">{errors.shortDescription.message}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="description" className="form-label required">Description</label>
                <textarea
                  id="description"
                  className={`form-textarea ${errors.description ? 'error' : ''}`}
                  placeholder="Detailed product description"
                  rows={5}
                  {...register('description')}
                />
                {errors.description && <span className="form-error">{errors.description.message}</span>}
              </div>
            </div>

            <div className="form-section">
              <h3>Pricing</h3>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="price" className="form-label required">Price (£)</label>
                  <input
                    id="price"
                    type="number"
                    step="0.01"
                    className={`form-input ${errors.price ? 'error' : ''}`}
                    placeholder="0.00"
                    {...register('price')}
                  />
                  {errors.price && <span className="form-error">{errors.price.message}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="compareAtPrice" className="form-label">Compare at Price (£)</label>
                  <input
                    id="compareAtPrice"
                    type="number"
                    step="0.01"
                    className={`form-input ${errors.compareAtPrice ? 'error' : ''}`}
                    placeholder="0.00"
                    {...register('compareAtPrice')}
                  />
                  {errors.compareAtPrice && <span className="form-error">{errors.compareAtPrice.message}</span>}
                  <span className="form-hint">Original price to show as crossed out</span>
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3>Product Details</h3>

              <div className="form-group">
                <label htmlFor="artist" className="form-label">Artist</label>
                <input
                  id="artist"
                  type="text"
                  className="form-input"
                  placeholder="Artist name"
                  {...register('artist')}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="dimensions" className="form-label">Dimensions</label>
                  <input
                    id="dimensions"
                    type="text"
                    className="form-input"
                    placeholder="e.g., 60cm x 40cm"
                    {...register('dimensions')}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="materials" className="form-label">Materials</label>
                  <input
                    id="materials"
                    type="text"
                    className="form-input"
                    placeholder="e.g., Canvas print"
                    {...register('materials')}
                  />
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3>Features & Specifications</h3>
              <div className="form-group">
                <label className="form-label">
                  Product Features
                  <span className="form-hint">Add key specifications like weight, color, materials, etc.</span>
                </label>
                <FeaturesEditor features={features} onChange={setFeatures} />
              </div>
            </div>

            <div className="form-section">
              <h3>Tags</h3>
              <div className="form-group">
                <label className="form-label">
                  Product Tags
                  <span className="form-hint">Add tags to help customers find this product</span>
                </label>
                <TagsInput tags={tags} onChange={setTags} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab Panel: Content */}
      {activeTab === 'content' && (
        <div className="form-grid single-column">
          <div className="form-column main-column">
            <div className="form-section">
              <h3>About the Art</h3>
              <div className="form-group">
                <label className="form-label">
                  About the Art
                  <span className="form-hint">Rich content describing the artwork, its history, and significance</span>
                </label>
                <RichTextEditor
                  value={watch('aboutTheArt')}
                  onChange={(content) => setValue('aboutTheArt', content)}
                  placeholder="Enter detailed information about the artwork..."
                />
              </div>
            </div>

            <div className="form-section">
              <h3>Additional Information</h3>
              <div className="form-group">
                <label className="form-label">
                  Additional Information
                  <span className="form-hint">Any other relevant details about the product</span>
                </label>
                <RichTextEditor
                  value={watch('additionalInformation')}
                  onChange={(content) => setValue('additionalInformation', content)}
                  placeholder="Enter additional information..."
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab Panel: Media */}
      {activeTab === 'media' && (
        <div className="form-grid single-column">
          <div className="form-column main-column">
            <div className="form-section">
              <h3>Product Images</h3>
              <p className="form-hint" style={{ marginBottom: '1rem' }}>
                Upload product images. The first image will be set as the primary image by default.
                You can reorder images by dragging and click the star icon to set a different primary image.
              </p>
              <ImageUpload images={images} onChange={setImages} />
              {images.length === 0 && (
                <p className="form-hint" style={{ marginTop: '0.5rem', color: '#d97706' }}>
                  Tip: Adding at least one product image will help customers visualize the product.
                </p>
              )}
            </div>

            <div className="form-section">
              <h3>Video Content</h3>

              <div className="form-group">
                <label htmlFor="videoUrl" className="form-label">Video URL</label>
                <input
                  id="videoUrl"
                  type="url"
                  className="form-input"
                  placeholder="https://youtube.com/watch?v=..."
                  {...register('videoUrl')}
                />
              </div>

              <div className="form-group">
                <label htmlFor="videoType" className="form-label">Video Type</label>
                <select
                  id="videoType"
                  className="form-select"
                  {...register('videoType')}
                >
                  <option value="">Select type</option>
                  <option value="youtube">YouTube</option>
                  <option value="vimeo">Vimeo</option>
                  <option value="mp4">MP4 (Direct Link)</option>
                  <option value="webm">WebM (Direct Link)</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="videoThumbnailUrl" className="form-label">Video Thumbnail URL</label>
                <input
                  id="videoThumbnailUrl"
                  type="url"
                  className="form-input"
                  placeholder="https://..."
                  {...register('videoThumbnailUrl')}
                />
                <span className="form-hint">Optional: Auto-fetched for YouTube/Vimeo if left blank</span>
              </div>

              <div className="form-group">
                <label className="form-checkbox">
                  <input type="checkbox" {...register('hasVideoContent')} />
                  <span className="checkbox-label">Has video content</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tab Panel: Settings */}
      {activeTab === 'settings' && (
        <div className="form-grid single-column">
          <div className="form-column main-column">
            <div className="form-section">
              <h3>Status & Organization</h3>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="status" className="form-label required">Product Status</label>
                  <select
                    id="status"
                    className={`form-select ${errors.status ? 'error' : ''}`}
                    {...register('status')}
                  >
                    <option value="DRAFT">Draft</option>
                    <option value="ACTIVE">Active</option>
                    <option value="ARCHIVED">Archived</option>
                    <option value="OUT_OF_STOCK">Out of Stock</option>
                  </select>
                  {errors.status && <span className="form-error">{errors.status.message}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="category" className="form-label required">Category</label>
                  <select
                    id="category"
                    className={`form-select ${errors.category ? 'error' : ''}`}
                    {...register('category')}
                    disabled={categoriesLoading}
                  >
                    <option value="">
                      {categoriesLoading ? 'Loading categories...' : 'Select a category'}
                    </option>
                    {renderCategoryOptions()}
                  </select>
                  {errors.category && <span className="form-error">{errors.category.message}</span>}
                  {categoriesError && <span className="form-error">{categoriesError}</span>}
                  {!categoriesLoading && categories.length === 0 && !categoriesError && (
                    <span className="form-hint" style={{ color: '#d97706' }}>
                      No categories available. Please create categories first.
                    </span>
                  )}
                </div>
              </div>
            </div>

            <div className="form-section">
              <h3>Inventory</h3>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="stock" className="form-label required">Stock Quantity</label>
                  <input
                    id="stock"
                    type="number"
                    className={`form-input ${errors.stock ? 'error' : ''}`}
                    placeholder="0"
                    {...register('stock')}
                  />
                  {errors.stock && <span className="form-error">{errors.stock.message}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="lowStockThreshold" className="form-label">Low Stock Threshold</label>
                  <input
                    id="lowStockThreshold"
                    type="number"
                    className={`form-input ${errors.lowStockThreshold ? 'error' : ''}`}
                    placeholder="5"
                    {...register('lowStockThreshold')}
                  />
                  {errors.lowStockThreshold && <span className="form-error">{errors.lowStockThreshold.message}</span>}
                </div>
              </div>

              <div className="form-group">
                <label className="form-checkbox" style={{ display: 'grid', gridTemplateColumns: '20px 1fr', width: '100%' }}>
                  <input type="checkbox" {...register('isLimitedEdition')} />
                  <span className="checkbox-label">This is a limited edition product</span>
                </label>
              </div>

              {isLimitedEdition && (
                <div className="form-group">
                  <label htmlFor="editionSize" className="form-label">Edition Size</label>
                  <input
                    id="editionSize"
                    type="number"
                    className={`form-input ${errors.editionSize ? 'error' : ''}`}
                    placeholder="e.g., 100"
                    {...register('editionSize')}
                  />
                  {errors.editionSize && <span className="form-error">{errors.editionSize.message}</span>}
                </div>
              )}
            </div>

            <div className="form-section">
              <h3>Featured Product</h3>

              <div className="form-group">
                <label className="form-checkbox" style={{ display: 'grid', gridTemplateColumns: '20px 1fr', width: '100%' }}>
                  <input type="checkbox" {...register('isFeatured')} />
                  <span className="checkbox-label">Feature this product on homepage</span>
                </label>
                <span className="form-hint" style={{ marginLeft: '26px' }}>
                  Featured products appear in the homepage carousel and featured collections.
                </span>
              </div>
            </div>

            <div className="form-section">
              <h3>Accessibility</h3>

              <div className="form-group">
                <label className="form-checkbox" style={{ display: 'grid', gridTemplateColumns: '20px 1fr', width: '100%' }}>
                  <input type="checkbox" {...register('hasAudioDescription')} />
                  <span className="checkbox-label">Has audio description</span>
                </label>
              </div>

              <div className="form-group">
                <label htmlFor="audioUrl" className="form-label">Audio Description URL</label>
                <input
                  id="audioUrl"
                  type="url"
                  className="form-input"
                  placeholder="https://cdn.example.com/audio/..."
                  {...register('audioUrl')}
                />
              </div>

              <div className="form-group">
                <label htmlFor="tactileMockupUrl" className="form-label">Tactile Mockup URL</label>
                <input
                  id="tactileMockupUrl"
                  type="url"
                  className="form-input"
                  placeholder="https://cdn.example.com/tactile/..."
                  {...register('tactileMockupUrl')}
                />
                <span className="form-hint">3D model file (.stl, .obj) for tactile exploration</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Keyboard Shortcuts Hint */}
      <div className="keyboard-shortcuts-hint">
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
        </svg>
        <span>Keyboard shortcuts:</span>
        <kbd>{navigator.platform.toUpperCase().indexOf('MAC') >= 0 ? 'Cmd' : 'Ctrl'} + S</kbd> to save
        <kbd>{navigator.platform.toUpperCase().indexOf('MAC') >= 0 ? 'Cmd' : 'Ctrl'} + Enter</kbd> to publish
        <kbd>Esc</kbd> to close
      </div>

      <div className="form-actions">
        <div className="form-actions-left">
          <button type="button" className="btn btn-secondary" onClick={() => window.history.back()}>
            Cancel
          </button>
          {!isEditing && templates.length > 0 && (
            <button type="button" className="btn btn-secondary" onClick={() => setShowLoadTemplateModal(true)}>
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z" />
              </svg>
              Load Template
            </button>
          )}
        </div>
        <div className="form-actions-right">
          <button type="button" className="btn btn-secondary" onClick={() => setShowSaveTemplateModal(true)}>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M17.593 3.322c1.1.128 1.907 1.077 1.907 2.185V21L12 17.25 4.5 21V5.507c0-1.108.806-2.057 1.907-2.185a48.507 48.507 0 0111.186 0z" />
            </svg>
            Save as Template
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => setShowPreview(true)}>
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Preview
          </button>
          <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : isEditing ? 'Update Product' : 'Create Product'}
          </button>
        </div>
      </div>

      {/* Product Preview Modal */}
      <ProductPreview
        isOpen={showPreview}
        onClose={() => setShowPreview(false)}
        productData={{
          ...watch(),
          images,
          features,
          tags,
          category: categories.find(c => c.id === watch('category')),
        }}
      />

      {/* Save Template Modal */}
      {showSaveTemplateModal && (
        <div className="modal-overlay" onClick={() => setShowSaveTemplateModal(false)}>
          <div className="modal-content template-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Save as Template</h3>
              <button className="modal-close" onClick={() => setShowSaveTemplateModal(false)} aria-label="Close">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="modal-body">
              <p className="modal-text">
                Save the current product configuration as a reusable template. This will save all fields except SKU, images, and stock levels.
              </p>
              <div className="form-group">
                <label className="form-label">Template Name</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g., Standard Artwork Product"
                  value={templateName}
                  onChange={(e) => setTemplateName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSaveAsTemplate();
                    }
                  }}
                  autoFocus
                />
              </div>
            </div>

            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowSaveTemplateModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleSaveAsTemplate}>
                Save Template
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Load Template Modal */}
      {showLoadTemplateModal && (
        <div className="modal-overlay" onClick={() => setShowLoadTemplateModal(false)}>
          <div className="modal-content template-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Load Template</h3>
              <button className="modal-close" onClick={() => setShowLoadTemplateModal(false)} aria-label="Close">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="modal-body">
              <p className="modal-text">
                Select a template to pre-fill the product form. You can modify any fields after loading.
              </p>

              {templates.length === 0 ? (
                <div className="empty-state-small">
                  <p>No templates saved yet. Create your first template by filling out a product form and clicking "Save as Template".</p>
                </div>
              ) : (
                <div className="template-list">
                  {templates.map((template) => (
                    <div key={template.id} className="template-item">
                      <div className="template-info">
                        <div className="template-name">{template.name}</div>
                        <div className="template-meta">
                          Created {new Date(template.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                      <div className="template-actions">
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => handleLoadTemplate(template.id)}
                        >
                          Load
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => handleDeleteTemplate(template.id)}
                          title="Delete template"
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={() => setShowLoadTemplateModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </form>
  );
}

export default ProductForm;
